import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button, Label } from '@/components/ui'
import type { ComboMainMeal as ComboMainMealType, ComboRow, ComboRowIngredient, MenuItem } from '@/types/menu'
import type { SelectedModifier, IngredientVariationSelection } from '@/types/cart'
import type { RuleEvaluationResult } from '@/types/logicRules'
import { resolveIngredientPrice, rowHasDynamicPricing } from '@/utils/ingredientPricing'
import { formatPrice } from '@/utils/formatting'
import { ComboMainMealRow } from './ComboMainMealRow'

interface ComboMainMealProps {
  mainMeal: ComboMainMealType
  customizations: SelectedModifier[]
  onCustomizationChange: (customizations: SelectedModifier[]) => void
  ingredientVariationSelections: IngredientVariationSelection[]
  onIngredientVariationChange: (selections: IngredientVariationSelection[]) => void
  ruleResult: RuleEvaluationResult
  getIngredientsToDisplay: (row: ComboRow) => ComboRowIngredient[]
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  menuItem: MenuItem
}

export function ComboMainMeal({
  mainMeal,
  customizations,
  onCustomizationChange,
  ingredientVariationSelections,
  onIngredientVariationChange,
  ruleResult,
  getIngredientsToDisplay,
  expanded,
  onExpandedChange,
  menuItem,
}: ComboMainMealProps) {
  // Handle selecting/changing a variation for a row ingredient
  const handleVariationSelect = (
    row: ComboRow,
    ing: ComboRowIngredient,
    variationId: string,
    variationName: string,
    priceAdjustment: number
  ) => {
    const vg = ing.ingredient.variationGroup!
    // Replace any existing variation for this row ingredient
    const updated = ingredientVariationSelections.filter(
      (vs) => !(vs.rowId === row.id && vs.rowIngredientId === ing.id)
    )
    updated.push({
      rowId: row.id,
      rowIngredientId: ing.id,
      ingredientName: ing.ingredient.name,
      variationGroupId: vg.id,
      variationGroupName: vg.name,
      variationId,
      variationName,
      priceAdjustment,
    })
    onIngredientVariationChange(updated)
  }

  // Auto-select default variation when an ingredient with variations is selected
  const handleIngredientSelect = (
    row: ComboRow,
    ing: ComboRowIngredient,
    newCustomizations: SelectedModifier[]
  ) => {
    onCustomizationChange(newCustomizations)

    // Check if this ingredient was just selected (not deselected)
    const wasSelected = customizations.some(
      (m) => m.groupId === row.id && m.optionId === ing.id
    )
    if (!wasSelected && ing.ingredient.hasVariations && ing.ingredient.variationGroup) {
      const defaultVar = ing.ingredient.variationGroup.variations.find((v) => v.is_default)
      if (defaultVar) {
        handleVariationSelect(row, ing, defaultVar.id, defaultVar.name, defaultVar.price ?? 0)
      }
    }
  }

  // Compute display price for a combo row ingredient
  const getIngredientDisplayPrice = (
    row: ComboRow,
    ing: ComboRowIngredient
  ): { label: string; value: number } | null => {
    const isDynamic = rowHasDynamicPricing(menuItem, row.id)
    if (!isDynamic) return null // Static rows use ing.additionalPrice directly

    // Override ingredients always show their price
    const overridePrice = menuItem.customizationPrices?.[ing.id]
    if (overridePrice !== undefined) {
      return { label: `+${formatPrice(overridePrice, menuItem.currency, false)}`, value: overridePrice }
    }

    // Count non-override selections to determine position
    const selectedInRow = customizations.filter((m) => m.groupId === row.id)
    const nonOverrideSelected = selectedInRow.filter(
      (m) => !menuItem.customizationPrices?.[m.optionId]
    )
    const isSelected = nonOverrideSelected.some((m) => m.optionId === ing.id)
    const currentIndex = isSelected
      ? nonOverrideSelected.findIndex((m) => m.optionId === ing.id)
      : nonOverrideSelected.length

    const price = resolveIngredientPrice(menuItem, row.id, ing.id, currentIndex)
    if (price === 0) return { label: 'Included', value: 0 }
    return { label: `+${formatPrice(price, menuItem.currency, false)}`, value: price }
  }

  return (
    <div className="space-y-3 border rounded-lg p-4 bg-muted/20">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl">&#127828;</span>
          <div>
            <Label className="text-base font-semibold">Main Item</Label>
            <p className="text-sm text-muted-foreground">{mainMeal.title}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onExpandedChange(!expanded)}
          className="text-sm"
        >
          {expanded ? (
            <>
              Collapse <ChevronUp className="ml-1 h-4 w-4" />
            </>
          ) : (
            <>
              Customize <ChevronDown className="ml-1 h-4 w-4" />
            </>
          )}
        </Button>
      </div>

      {/* Show current selections summary when collapsed */}
      {!expanded && customizations.length > 0 && (
        <div className="text-sm text-muted-foreground pl-9">
          {customizations.map((mod, idx) => {
            const varSel = ingredientVariationSelections.find(
              (vs) => vs.rowId === mod.groupId && vs.rowIngredientId === mod.optionId
            )
            const qty = mod.quantity ?? 1
            return (
              <span key={mod.optionId}>
                {mod.groupName}: {qty > 1 ? `${qty}x ` : ''}{mod.optionName}
                {varSel && ` (${varSel.variationName})`}
                {idx < customizations.length - 1 && ' • '}
              </span>
            )
          })}
        </div>
      )}

      {/* Expanded: Show row customizations */}
      {expanded && mainMeal.rows && mainMeal.rows.length > 0 && (
        <div className="space-y-4 pt-2 border-t mt-3">
          {[...mainMeal.rows]
            .sort((a, b) => a.order - b.order)
            .map((row) => (
              <ComboMainMealRow
                key={row.id}
                row={row}
                customizations={customizations}
                onCustomizationChange={onCustomizationChange}
                ingredientVariationSelections={ingredientVariationSelections}
                ruleResult={ruleResult}
                getIngredientsToDisplay={getIngredientsToDisplay}
                menuItem={menuItem}
                onVariationSelect={handleVariationSelect}
                onIngredientSelect={handleIngredientSelect}
                getIngredientDisplayPrice={getIngredientDisplayPrice}
              />
            ))}
        </div>
      )}
    </div>
  )
}
